import React from "react";
import { motion } from "framer-motion";

const EULA = () => {
  return (
    <div className="min-h-screen bg-gray-100 py-12 px-6 md:px-20">
      <div className="max-w-5xl mx-auto bg-white shadow-lg rounded-lg p-10">
        {/* Page Heading */}
        <motion.h1
          className="text-4xl font-extrabold text-purple-700 text-center mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          End User License Agreement
        </motion.h1>

        <p className="text-gray-500 text-center mb-8">
          Last Updated: <strong>April 27, 2024</strong>
        </p>

        {/* Section */}
        <motion.section
          className="space-y-8 text-gray-700"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          {/* 1. Introduction */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">1. Introduction</h2>
            <p>
              This End User License Agreement (EULA) is a legal agreement between you ("User") and Pawdash 
              ("we," "us," "our") governing your use of the Pawdash mobile application and website (collectively, the "Software" or "Services").
            </p>
            <p>
              By downloading, installing, accessing, or using the Software, you agree to be bound by this EULA. If you do not agree, please discontinue use immediately.
            </p>
          </div>

          {/* 2. License Grant */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">2. License Grant</h2>
            <p>
              Subject to your compliance with this EULA, Pawdash grants you a limited, non-exclusive, non-transferable license to download, install, 
              and use the Software for personal, non-commercial purposes on compatible devices owned or controlled by you within the UAE.
            </p>
          </div>

          {/* 3. Restrictions on Use */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">3. Restrictions on Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>🚫 Copy, modify, or create derivative works of the Software.</li>
              <li>🔍 Reverse engineer, decompile, or disassemble the Software.</li>
              <li>⚠️ Use the Software for any unlawful or harmful purposes.</li>
              <li>🔒 Attempt to gain unauthorized access to any systems or data.</li>
            </ul>
          </div>

          {/* 4. Ownership and IP */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">4. Ownership and Intellectual Property</h2>
            <p>
              Pawdash and its licensors retain full rights to the Software, including all intellectual property rights. This EULA does not 
              grant you any ownership rights beyond this limited license.
            </p>
          </div>

          {/* 5. Updates and Modifications */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">5. Updates and Modifications</h2>
            <p>
              We may release updates, patches, or modifications to improve the Software. Pawdash is not obligated to provide updates, 
              but if we do, this EULA will apply to all updates.
            </p>
          </div>

          {/* 6. Privacy and Data Collection */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">6. Privacy and Data Collection</h2>
            <p>
              Your use of the Software is subject to our Privacy Policy. We collect and use data to improve our Services. 
              Learn more here: <a href="/privacy-policy" className="text-blue-600 underline">Privacy Policy</a>.
            </p>
          </div>

          {/* 7. Termination */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">7. Termination</h2>
            <p>
              You may terminate this EULA by uninstalling the Software. Pawdash may terminate your access if you breach any of these terms.
            </p>
          </div>

          {/* 8. Disclaimer of Warranties */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">8. Disclaimer of Warranties</h2>
            <p>
              The Software is provided "as is" without warranties of any kind. Pawdash disclaims all implied warranties, including merchantability and fitness for a particular purpose.
            </p>
          </div>

          {/* 9. Limitation of Liability */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">9. Limitation of Liability</h2>
            <p>
              Pawdash shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Software.
            </p>
          </div>

          {/* 10. Indemnification */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">10. Indemnification</h2>
            <p>
              You agree to indemnify and hold Pawdash harmless from any claims, damages, or losses arising from your use of the Software.
            </p>
          </div>

          {/* 11. Governing Law */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">11. Governing Law</h2>
            <p>
              This EULA is governed by the laws of the United Arab Emirates (UAE).
            </p>
          </div>

          {/* 12. Contact Us */}
          <div>
            <h2 className="text-2xl font-semibold text-purple-600 mb-3">12. Contact Us</h2>
            <ul className="list-disc pl-6">
              <li>📧 Email: info@mypawdash.com</li>
              <li>📍 Address: Abu Dhabi, UAE</li>
              <li>📞 Phone: +971 509 921 927</li>
            </ul>
          </div>
        </motion.section>

        {/* Back to Home Button */}
        <div className="mt-8 flex justify-center">
          <motion.a
            href="/"
            className="px-6 py-3 bg-purple-700 text-white text-lg rounded-lg shadow-lg hover:bg-purple-800 transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Back to Home
          </motion.a>
        </div>
      </div>
    </div>
  );
};

export default EULA;
